import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { Icon } from '@/components/ui/Icon';
import { SoftphonePanel } from './SoftphonePanel';
import { useSoftphoneWindow } from '../stores/softphoneWindow';
import { useSipStore, type SipState } from '../stores/sipStore';
import { useCustomerCard } from '../stores/customerCard';

const WIDTH = 320;

// מצבים שבהם חייבים לראות את הטלפון — שיחה מצלצלת/פעילה, וגם 'wrap-up':
// ברגע שהשיחה נגמרה החלון נעלם בדיוק כשצריך אותו לתיעוד ולחיוג הבא. זה
// בלט בקמפיין predictive, שם רוב השיחות מסתיימות תוך שניות
// (batch_won_elsewhere) והחייגן "קפץ ונעלם".
const IN_CALL_STATES: SipState[] = ['ringing-in', 'ringing-out', 'on-call', 'on-hold', 'wrap-up'];

// מסכים שכבר מציגים פאנל סופטפון מעוגן משלהם.
const DOCKED_HOST_ROUTES = ['/agent', '/agent/active'];

// מסך התותח: הסופטפון הצף לא מוצג בו כלל (24/09/2026). הוא כיסה את
// הלידים, הציע חייגן ידני באמצע סבב, ובלבל — "זה לא צריך להופיע".
// פקדי השיחה (השתק/המתנה/נתק) יושבים שם בסרגל העליון (CallControls).
const HIDDEN_ROUTES = ['/admin/campaign', '/agent/campaign'];

/**
 * Detached softphone that floats above every agent screen. Rendered once
 * globally from AgentLayout, so the live call + dialpad persist while the
 * agent navigates. Draggable by its header; "dock" returns it into the page.
 */
export function FloatingSoftphone() {
  const floating = useSoftphoneWindow((s) => s.floating);
  const setFloating = useSoftphoneWindow((s) => s.setFloating);
  const x = useSoftphoneWindow((s) => s.x);
  const y = useSoftphoneWindow((s) => s.y);
  const setPos = useSoftphoneWindow((s) => s.setPos);

  const [pos, setLocalPos] = useState({ x, y });
  const drag = useRef<{ dx: number; dy: number } | null>(null);

  // כשיש שיחה (מצלצלת/פעילה) והסופטפון מעוגן אבל המסך הנוכחי לא מציג
  // פאנל מעוגן — מציגים את הצף בכל זאת. בלי זה נציג בעמוד החייגן (או מנהל
  // בכל מסך) לא רואה בכלל שהוא בשיחה.
  const sipState = useSipStore((s) => s.state);
  const { pathname } = useLocation();
  const onDockedHost = DOCKED_HOST_ROUTES.some((r) => pathname === r || pathname === r + '/');
  const onHiddenRoute = HIDDEN_ROUTES.some((r) => pathname === r || pathname === r + '/');
  const autoShow = !floating && IN_CALL_STATES.includes(sipState) && !onDockedHost;

  // החלון נשאר פתוח אחרי שהשיחה נגמרה, עד שהמשתמש יסגור אותו בעצמו.
  // בלי זה הוא נעלם ברגע שהמצב חוזר ל-'ready' — הנציג נשאר בלי חייגן,
  // בלי המספר שחייג אליו, ובלי דרך לתעד או לחייג שוב.
  const [stickOpen, setStickOpen] = useState(false);
  useEffect(() => {
    if (autoShow) setStickOpen(true);
  }, [autoShow]);

  // "סיים סיכום והתפנה לשיחה הבאה" מסיים את הסיכום וסוגר את כרטיס הלקוח.
  // בלי איפוס ה-stick, ברגע שהכרטיס נסגר החלון הצף היה צץ מיד במקומו —
  // הנציג ביקש להתפנות, לא לקבל חלון אחר.
  //
  // הדגל הוא "הכרטיס אירח את השיחה", ולא cardOpen עצמו: כשה-effect רץ
  // הכרטיס כבר נסגר, ובדיקה על cardOpen הנוכחי לעולם לא הייתה מתקיימת.
  // כך גם מי שפתח את הצף בעצמו (בלי כרטיס) לא מאבד אותו בכל מעבר ל-'ready'.
  const cardOpen = useCustomerCard((s) => s.open);
  const cardHostedCall = useRef(false);
  useEffect(() => {
    if (cardOpen) cardHostedCall.current = true;
  }, [cardOpen]);
  useEffect(() => {
    if (sipState === 'ready' && cardHostedCall.current) {
      cardHostedCall.current = false;
      setStickOpen(false);
    }
  }, [sipState]);

  // כרטיס הלקוח מארח את הסופטפון בתוכו (עמודת השיחה החיה), והוא מודל חוסם
  // ב-z-index 1300 מעל הצף (1100) — בלי ההסתרה הזו הצף נקבר מתחת לרקע הכהה
  // ונוצרים שני חייגנים, אחד מהם לא לחיץ. אותה תבנית כמו ב-WrapupOverlay.
  const visible = !onHiddenRoute && !cardOpen && (floating || autoShow || (stickOpen && !onDockedHost));

  // Keep the window inside the viewport (e.g. after a resize / first mount).
  useEffect(() => {
    if (!visible) return;
    const clampX = Math.min(Math.max(8, x), window.innerWidth - WIDTH - 8);
    const clampY = Math.min(Math.max(8, y), window.innerHeight - 120);
    setLocalPos({ x: clampX, y: clampY });
  }, [visible, x, y]);

  if (!visible) return null;

  function onPointerDown(e: React.PointerEvent) {
    // Don't start a drag from a button/input inside the header.
    if ((e.target as HTMLElement).closest('button')) return;
    drag.current = { dx: e.clientX - pos.x, dy: e.clientY - pos.y };
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
  }
  function onPointerMove(e: React.PointerEvent) {
    if (!drag.current) return;
    const nx = Math.min(Math.max(8, e.clientX - drag.current.dx), window.innerWidth - WIDTH - 8);
    const ny = Math.min(Math.max(8, e.clientY - drag.current.dy), window.innerHeight - 120);
    setLocalPos({ x: nx, y: ny });
  }
  function onPointerUp(e: React.PointerEvent) {
    if (drag.current) { setPos(pos.x, pos.y); drag.current = null; }
    (e.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
  }

  return createPortal(
    <div
      role="dialog"
      aria-label="סופטפון צף"
      style={{
        // Use physical `left` (always measured from the viewport's left edge),
        // NOT insetInlineStart — in this RTL document the logical-start edge is
        // the RIGHT, which inverted the drag direction. pos.x comes from clientX
        // which is also left-based, so they must match.
        position: 'fixed', left: pos.x, top: pos.y, width: WIDTH, zIndex: 1100,
        background: 'var(--surface)', borderRadius: 'var(--r-3)',
        border: '1px solid var(--line)', boxShadow: 'var(--shadow-3)',
        overflow: 'hidden',
      }}
    >
      {/* Drag handle */}
      <div
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', cursor: 'move', userSelect: 'none',
          background: 'var(--surface-2)', borderBottom: '1px solid var(--line)',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>
          <Icon name="phone" size={12} /> {autoShow ? 'שיחה פעילה' : 'גרור להזזה'}
        </span>
        {!autoShow && (
          <button
            type="button"
            className="btn ghost"
            onClick={() => { setStickOpen(false); setFloating(false); }}
            aria-label="עגן למסך"
            title="עגן למסך"
            style={{ height: 24, width: 24, padding: 0 }}
          >
            <Icon name="target" size={14} />
          </button>
        )}
      </div>
      <div style={{ padding: 'var(--pad-card)' }}>
        <SoftphonePanel bare hideFloatToggle />
      </div>
    </div>,
    document.body
  );
}
